import { Link } from "react-router-dom";
import Reveal from "@/components/Reveal";
import { usePageMeta } from "@/lib/meta";
import { PROCESS_STEPS } from "@/lib/site";

export default function Process() {
  usePageMeta(
    "The Turnkey Process — From Consultation to Handover | Shriya Redkar Interiors",
    "Consultation → 3D with real material textures → fixed BOQ → execution by 6 squads → white-glove handover. The render-overs objection, answered."
  );

  return (
    <main className="pt-[76px]">
      <section className="container-x py-16 lg:py-24">
        <Reveal>
          <p className="eyebrow">Services &amp; process</p>
          <h1 className="mt-5 max-w-4xl font-serif text-4xl font-light leading-[1.08] text-zinc-50 sm:text-5xl lg:text-6xl">
            Five steps between "good idea" and{" "}
            <span className="italic text-gold">handover day.</span>
          </h1>
          <p className="mt-6 max-w-2xl text-base leading-relaxed text-zinc-400">
            One studio owns everything — design, BOQ, execution, styling. You approve one 3D, and that 3D
            is the contract the site is built against.
          </p>
        </Reveal>
      </section>

      <section className="container-x pb-8">
        {PROCESS_STEPS.map((s, i) => (
          <Reveal key={s.n} delay={0.05}>
            <div className="grid grid-cols-12 gap-4 border-t border-white/10 py-10 lg:py-14" data-testid={`process-detail-step-${s.n}`}>
              <span className="col-span-12 font-serif text-5xl text-gold/60 lg:col-span-2 lg:text-7xl">{s.n}</span>
              <h2 className="col-span-12 font-serif text-2xl leading-tight text-zinc-50 sm:text-3xl lg:col-span-4">{s.title}</h2>
              <p className="col-span-12 text-base leading-relaxed text-zinc-400 lg:col-span-6">{s.desc}</p>
            </div>
          </Reveal>
        ))}
      </section>

      {/* MATERIALS */}
      <section className="border-y border-white/10 bg-obsidian-soft">
        <div className="container-x grid items-center gap-12 py-20 lg:grid-cols-12 lg:py-28">
          <Reveal className="lg:col-span-6">
            <div className="frame-clipped">
              <img
                src="/images/site/flatlay.jpg"
                alt="Real material samples — stone, wood, brass and fabric"
                loading="lazy"
                className="aspect-square w-full object-cover"
                data-testid="process-materials-image"
              />
            </div>
          </Reveal>
          <div className="lg:col-span-5 lg:col-start-8">
            <Reveal>
              <p className="eyebrow">Step two, in physical form</p>
              <h2 className="mt-5 font-serif text-3xl font-light leading-tight text-zinc-50 sm:text-4xl">
                This is what "real materials in 3D" means.
              </h2>
              <p className="mt-6 text-base leading-relaxed text-zinc-400">
                Most studios render with approximations, then hope the market has your stone. We texture
                your 3D with the exact travertine, veneer, brass and fabric samples you've touched — so the
                render you approve is a photograph of your home's future, not an artist's guess.
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      {/* OBJECTION */}
      <section className="container-x py-24 lg:py-32">
        <Reveal>
          <div className="mx-auto max-w-4xl text-center">
            <p className="eyebrow">The objection, answered</p>
            <h2 className="mt-6 font-serif text-3xl font-light leading-snug text-zinc-100 sm:text-5xl">
              "Every studio shows beautiful renders.{" "}
              <span className="italic text-gold">How do I know the site will match?</span>"
            </h2>
            <p className="mx-auto mt-8 max-w-2xl text-base leading-relaxed text-zinc-400">
              Because we don't ask you to trust renders — we ask you to judge results. Every project we've
              delivered is published as the approved render beside the finished photo, with an honest caption
              about lighting, site surprises and change requests. Materials, timelines and budgets stay on
              record too.
            </p>
            <div className="mt-12 flex flex-wrap items-center justify-center gap-4">
              <Link to="/portfolio" data-testid="process-cta-portfolio" className="btn-gold">
                Judge the portfolio
              </Link>
              <Link to="/enquire" data-testid="process-cta-enquire" className="btn-ghost">
                Start with a consultation
              </Link>
            </div>
          </div>
        </Reveal>
      </section>
    </main>
  );
}
